import { SCENARIOS, SIZE_PREFS, SOLO_SPEED_IDS, rankVerdict, scenarioByChoice, scenarioRulesLine, scenarioSeats, speedById, type ScenarioChoiceId, type SizePref, type SpeedId, type SpeedRung } from '@kolony/shared';
import { mountRankCheck, type RankCheckState } from './rankCheck';

// The Play panel — the front door's one decision point.
//
// D216 shipped it as three lanes (Ranked, Casual, Bots), on the reasoning that they were
// three promises about stakes. D288 retires that, by the user's direction: whether a game
// counts is no longer a LANE you pick but a FACT about the table — it rates when it starts
// with at least two humans, bots filling the rest. So there is one card, "Find a game", with
// three axes (opponents, table size, speed), and a ranked CHECKMARK that says what these
// settings allow, with an ⓘ that says why.
//
// "Bots only" is the on-device solo table (a game with no other human from the start); its
// board choice survives from H20-S7 for the one size the Open Sea scenarios are built for.
//
// House rules: injected document, ports not services, every string via textContent
// (T-SEC-006), no default export, no import of net/ or game/ (§16.1 fence).

export type Opponents = 'humans' | 'bots';

/** What the panel remembers, and what the home button wears as its subtitle. */
export interface PlaySelection {
  opponents: Opponents;
  /** The pace — for a queued table and a solo table alike. */
  speed: SpeedId;
  size: SizePref;
  /**
   * H20-S7: the board the last SOLO game was dealt on — so "Play again" can repeat a scenario.
   * Only offered at 3–4 players (the scenarios have no other layouts). Absent is classic.
   */
  soloBoard?: SoloBoardId;
}

/** A solo table's board: the classic island, or one of the Open Sea scenarios. */
export type SoloBoardId = 'classic' | ScenarioChoiceId;

/** Resolve a stored board onto one that exists — a retired or hand-edited id plays classic. */
export function soloBoardOf(stored: string | undefined | null): SoloBoardId {
  return typeof stored === 'string' && scenarioByChoice(stored) !== null ? (stored as ScenarioChoiceId) : 'classic';
}

/** "Outer Isles", or nothing for the classic island. */
const boardLabel = (board: SoloBoardId): string | null => (board === 'classic' ? null : (scenarioByChoice(board)?.label ?? null));

/**
 * D288: how many seats a SOLO table deals for a size choice — the queue matches a RANGE, a
 * local table needs a number. '3-4' is the classic four; '5+' is six on the scaled board.
 */
export function soloSeatsFor(size: SizePref): number {
  if (size === '2') return 2;
  if (size === '3-4') return 4;
  return 6;
}

/**
 * The board a solo table at this size plays. A scenario is only offered — and only honoured —
 * at 3–4, where it has a layout; anything else plays classic, which the flow fits to the table
 * (the scaled board for 2 and for 5+).
 */
export function soloBoardFor(sel: PlaySelection): SoloBoardId {
  if (sel.size !== '3-4') return 'classic';
  const board = soloBoardOf(sel.soloBoard);
  const scenario = board === 'classic' ? null : scenarioByChoice(board);
  return scenario !== null && soloSeatsFor(sel.size) <= scenarioSeats(scenario).max ? board : 'classic';
}

export interface FindPick {
  speed: SpeedId;
  size: SizePref;
}

export interface PlayPanelOpts {
  doc: Document;
  /** Read on every open, so a second tab's choice is never overwritten by a stale one. */
  read(): PlaySelection;
  write(next: PlaySelection): void;
  /** Queue for a table of humans at these picks; bots fill whatever the queue cannot. */
  onFind(pick: FindPick): void;
  /** Start a solo table against bots — this pace, this board, this many seats — right now. */
  onSolo(speed: SpeedRung, board: SoloBoardId, seats: number): void;
  /** The shipped pass-and-play mode, which otherwise hides behind `?hotseat=N`. */
  onPassAndPlay?(): void;
  /** Signed out ⇒ the card says where a guest's rank actually lives (D200). */
  signedIn(): boolean;
  /**
   * D216/S5: games played this week. `null` ⇒ render NOTHING. Never "0 games" and never a
   * placeholder: a front door that admits it is empty is worse than one that says nothing.
   */
  activity?(): number | null;
}

export interface PlayPanel {
  readonly root: HTMLElement;
  open(): void;
  close(): void;
  isOpen(): boolean;
  /** The current selection, for the home button's subtitle. */
  selection(): PlaySelection;
  dispose(): void;
}

/** The speeds the queue is split by — the same three a solo table offers. Mail is a room setting. */
const FIND_SPEED_IDS: readonly SpeedId[] = SOLO_SPEED_IDS;

export const SIZE_LABELS: Readonly<Record<SizePref, string>> = { '2': '2 players', '3-4': '3–4 players', '5+': '5+ players' };

const OPPONENT_OPTIONS: ReadonlyArray<{ id: Opponents; label: string }> = [
  { id: 'humans', label: 'Humans' },
  { id: 'bots', label: 'Bots only' },
];

/**
 * D288: what the checkmark says for a selection. A HUMANS table is ranked by construction —
 * it only starts once a second human is there or the queue has bots finish it, and a table
 * where nobody else ever came is the one case that does not count. BOTS ONLY never rates:
 * nobody but you is human. The wording of the second comes from the shared rule itself.
 */
export function selectionRank(sel: PlaySelection): RankCheckState {
  if (sel.opponents === 'bots') return { ranked: false, reason: rankVerdict(1).reason };
  return {
    ranked: true,
    reason:
      'Ranked once at least one other human sits down with you. Bots fill any empty seats and never count. If nobody else turns up, the game is played but not ranked.',
  };
}

/**
 * D221: the one-tap lane's WORDS and its DESTINATION, decided together.
 *
 * They were computed separately at first and disagreed within a minute of being looked at —
 * a label written by one rule and a behaviour by another is D190's lying affordance with extra
 * steps. It is pure, and it lives here rather than in `main.ts` because main is
 * coverage-excluded: the agreement between the two halves is the property worth testing.
 */
export interface PlayAgainPlan {
  title: string;
  sub: string;
  /** Where the one tap actually goes. The caller turns this into a flow; nothing else may. */
  lane: 'find' | 'solo';
  /** H20-S7: the board a solo tap deals — decided HERE with the words that name it (D221). */
  board?: SoloBoardId;
  /** D288: the seats a solo tap deals. */
  seats?: number;
}

export function playAgainPlan(sel: PlaySelection, played: boolean): PlayAgainPlan {
  // A first-timer is not offered "again" — they are offered the lane that works with nobody
  // else online, and this is the promise the copy makes, so it is the one that is kept.
  if (!played) return { title: 'Jump in', sub: 'Humans · Standard speed · 3–4 players · bots fill empty seats', lane: 'find' };
  if (sel.opponents === 'bots') return { title: 'Play again', sub: selectionSummary(sel), lane: 'solo', board: soloBoardFor(sel), seats: soloSeatsFor(sel.size) };
  return { title: 'Play again', sub: selectionSummary(sel), lane: 'find' };
}

/** "Humans · Standard · 3–4 players" — what the Play button wears when the panel is shut. */
export function selectionSummary(sel: PlaySelection): string {
  const pace = speedById(sel.speed).label;
  if (sel.opponents === 'bots') {
    const board = boardLabel(soloBoardFor(sel));
    return `Bots only · ${pace} · ${SIZE_LABELS[sel.size]}${board !== null ? ` · ${board}` : ''}`;
  }
  return `Humans · ${pace} · ${SIZE_LABELS[sel.size]}`;
}

export function mountPlayPanel(opts: PlayPanelOpts): PlayPanel {
  const d = opts.doc;
  /**
   * Resolve whatever was stored onto values that actually exist. Without this a retired or
   * hand-edited rung id renders a segmented row with NOTHING selected, and the start button
   * would dispatch that id to a route whose enum refuses it.
   */
  const normalise = (s: PlaySelection): PlaySelection => ({
    ...s,
    opponents: s.opponents === 'bots' ? 'bots' : 'humans',
    speed: FIND_SPEED_IDS.includes(speedById(s.speed).id) ? speedById(s.speed).id : 'standard',
    size: SIZE_PREFS.includes(s.size) ? s.size : '3-4',
    soloBoard: soloBoardOf(s.soloBoard),
  });
  let sel: PlaySelection = normalise(opts.read());

  const root = d.createElement('div');
  root.className = 'play-scrim';
  root.dataset['playPanel'] = '1';
  root.style.display = 'none';

  const panel = d.createElement('div');
  panel.className = 'play-panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-label', 'Find a game');
  root.append(panel);

  const heading = d.createElement('h2');
  heading.className = 'play-heading';
  heading.textContent = 'Find a game';
  panel.append(heading);

  const body = d.createElement('div');
  body.className = 'play-body';
  panel.append(body);

  const foot = d.createElement('div');
  foot.className = 'play-foot';
  foot.dataset['playActivity'] = '1';
  panel.append(foot);

  const pick = (next: Partial<PlaySelection>): void => {
    sel = { ...sel, ...next };
    opts.write(sel);
    paint();
  };

  // ── small builders ──────────────────────────────────────────────────────────
  const card = (title: string, fixed: string, hook: string): HTMLElement => {
    const c = d.createElement('div');
    c.className = 'play-card';
    c.dataset['playCard'] = hook;
    const h = d.createElement('h3');
    h.className = 'play-card-title';
    h.textContent = title;
    const f = d.createElement('p');
    f.className = 'play-card-fixed';
    f.textContent = fixed;
    c.append(h, f);
    return c;
  };

  /**
   * A segmented row. Every option is always PRESENT — a row that re-flows as options come
   * and go slides the next choice under a finger already aimed at the last one (D130).
   */
  const segmented = (
    labelText: string,
    options: ReadonlyArray<{ id: string; label: string }>,
    current: string,
    hook: string,
    pick: (id: string) => void,
  ): HTMLElement => {
    const row = d.createElement('div');
    row.className = 'play-seg-row';
    const cap = d.createElement('span');
    cap.className = 'play-seg-label';
    cap.textContent = labelText;
    const seg = d.createElement('div');
    seg.className = 'play-seg';
    seg.setAttribute('role', 'radiogroup');
    seg.setAttribute('aria-label', labelText);
    for (const o of options) {
      const b = d.createElement('button');
      b.type = 'button';
      b.className = o.id === current ? 'play-seg-opt play-seg-opt--on' : 'play-seg-opt';
      b.dataset[hook] = o.id;
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', o.id === current ? 'true' : 'false');
      b.textContent = o.label;
      b.addEventListener('click', () => pick(o.id));
      seg.append(b);
    }
    row.append(cap, seg);
    return row;
  };

  const startButton = (label: string, hook: string, go: () => void): HTMLButtonElement => {
    const b = d.createElement('button');
    b.type = 'button';
    b.className = 'play-start';
    b.dataset['playStart'] = hook;
    b.textContent = label;
    b.addEventListener('click', () => {
      api.close();
      go();
    });
    return b;
  };

  const note = (text: string, cls = 'play-note'): HTMLElement => {
    const p = d.createElement('p');
    p.className = cls;
    p.textContent = text;
    return p;
  };

  // ── the one card ─────────────────────────────────────────────────────────────
  const paintCard = (): void => {
    const c = d.createElement('div');
    c.className = 'play-card';
    c.dataset['playCard'] = 'find';
    c.append(
      segmented('Opponents', OPPONENT_OPTIONS, sel.opponents, 'playOpponents', (id) => pick({ opponents: id as Opponents })),
      segmented('Players', SIZE_PREFS.map((id) => ({ id, label: id === '5+' ? '5+' : id.replace('-', '–') })), sel.size, 'playSize', (id) =>
        pick({ size: id as SizePref }),
      ),
      segmented('Speed', FIND_SPEED_IDS.map((id) => ({ id, label: speedById(id).label })), sel.speed, 'playSpeed', (id) => pick({ speed: id as SpeedId })),
    );
    c.append(note(speedById(sel.speed).blurb));
    // the liquidity line, said out loud — the reason a split queue is survivable at all
    if (sel.opponents === 'humans') {
      c.append(note(sel.size === '5+' ? 'Played on a scaled map built for however many sit down. If nobody joins in 30 seconds, bots fill the empty seats.' : 'If nobody joins in 30 seconds, bots fill the empty seats.', 'play-fill-text'));
    } else {
      c.append(note(sel.size === '5+' ? 'You and five bots on a scaled map. Starts instantly.' : `You and ${soloSeatsFor(sel.size) - 1 === 1 ? 'one bot' : `${soloSeatsFor(sel.size) - 1} bots`}. Starts instantly.`, 'play-fill-text'));
    }
    const rank = mountRankCheck(d, selectionRank(sel));
    c.append(rank.root);
    // D200: a guest's rank is real and portable — the moment to say where it lives
    if (sel.opponents === 'humans' && !opts.signedIn()) c.append(note('Your rank lives in this browser — sign in to keep it everywhere.'));
    body.append(c);

    if (sel.opponents === 'humans') {
      body.append(startButton('Find a game', 'find', () => opts.onFind({ speed: sel.speed, size: sel.size })));
      return;
    }
    const deal = (board: SoloBoardId): void => {
      sel = { ...sel, soloBoard: board };
      opts.write(sel);
      opts.onSolo(speedById(sel.speed), board, soloSeatsFor(sel.size));
    };
    body.append(startButton('Deal me in', 'bots', () => deal('classic')));
    // H20-S7: the Open Sea, one card per scenario — only where a scenario has a layout
    if (sel.size === '3-4') {
      const sea = d.createElement('div');
      sea.className = 'play-sea';
      sea.dataset['playSea'] = '1';
      const seaCap = d.createElement('p');
      seaCap.className = 'play-seg-label';
      seaCap.textContent = 'Open Sea · you and three bots';
      sea.append(seaCap);
      for (const scenario of SCENARIOS) {
        const sc = card(scenario.label, scenarioRulesLine(scenario), `sea-${scenario.id}`);
        sc.classList.add('play-card--sea');
        sc.append(note(scenario.blurb));
        sc.append(startButton('Set sail', `sea-${scenario.id}`, () => deal(scenario.choiceId)));
        sea.append(sc);
      }
      body.append(sea);
    }
    if (opts.onPassAndPlay) {
      const pnp = d.createElement('button');
      pnp.type = 'button';
      pnp.className = 'play-ghost';
      pnp.dataset['playPassPlay'] = '1';
      pnp.textContent = 'Pass & play on this device';
      pnp.addEventListener('click', () => {
        api.close();
        opts.onPassAndPlay?.();
      });
      body.append(pnp);
    }
  };

  const paint = (): void => {
    body.replaceChildren(); // innerHTML is banned repo-wide (T-SEC-006)
    paintCard();

    const games = opts.activity?.() ?? null;
    foot.replaceChildren();
    // Absent, never zero: below the floor the server omits the number entirely, and the
    // honest render of "no number" is no line.
    if (games !== null) foot.append(note(`${games} games played this week`, 'play-foot-text'));
  };

  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && api.isOpen()) {
      e.preventDefault();
      api.close();
    }
  };
  d.addEventListener('keydown', onKey);
  root.addEventListener('click', (e) => {
    if (e.target === root) api.close(); // the scrim closes it; the panel does not
  });

  d.body.appendChild(root);
  paint();

  const api: PlayPanel = {
    root,
    open(): void {
      sel = normalise(opts.read()); // never show a pick another tab has since changed
      paint();
      root.style.display = 'flex';
      body.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
    },
    close(): void {
      root.style.display = 'none';
    },
    isOpen(): boolean {
      return root.style.display !== 'none';
    },
    selection(): PlaySelection {
      return sel;
    },
    dispose(): void {
      d.removeEventListener('keydown', onKey);
      root.remove();
    },
  };
  return api;
}
